import React, { useState } from 'react'
import { BiMessageAltError } from 'react-icons/bi'
import {
  Alert,
  AlertIcon,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  useToast,
} from 'ui'
import { useMessageTemplateEdit } from '../hooks/useMessageTemplateEdit'
import { useMessageTemplate } from '../hooks/useMessageTemplates'
import { IMessageTemplate } from '../providers/MessageTemplate.provider'
import { CreateMessageTemplate } from './CreateMessageTemplate'
import { CreateMessageTemplateV2 } from './CreateMessageTemplateV2'
import { MessageTemplateEditor } from './MessageTemplateEditor'
import { MessageTemplateEditorV2 } from './MessageTemplateEditorV2'
import { TimelineStyles } from './TimelineStyles'
import { TranslatorOnlyString } from './Translator'

const MessageTemplateGatewayStatus = (props: {
  gatewayStatus?: IMessageTemplate['gatewayStatus']
}) => {
  return (
    <div className='max-w-md w-full'>
      {(() => {
        switch (props.gatewayStatus) {
          case 'pending':
            return (
              <Alert className='rounded-lg' status='info'>
                <AlertIcon />
                El proveedor de mensajería está revisando la plantilla
              </Alert>
            )

          case 'rejected':
            return (
              <Alert className='items-start rounded-lg' status='error'>
                <AlertIcon className='mt-[5px]' />
                El proveedor de mensajería rechazó la plantilla. Para saber mas
                detalles, contacta con soporte.
              </Alert>
            )

          case 'approved':
            return (
              <Alert status='success'>
                <AlertIcon />
                El proveedor de mensajería acepto la plantilla, por lo que la
                plantilla sera tomada en cuenta.
              </Alert>
            )

          default:
            return null
        }
      })()}
    </div>
  )
}

const MessageTemplateEdit = (props: { messageTemplate: IMessageTemplate }) => {
  const { debounceSwitch, formId, isLoading, onUpdateMessageTemplate } =
    useMessageTemplateEdit(props)

  const { toast } = useToast()

  return (
    <div className='relative'>
      {props.messageTemplate.gatewayStatus && (
        <div>
          <MessageTemplateGatewayStatus
            gatewayStatus={props.messageTemplate.gatewayStatus}
          />
        </div>
      )}

      <div className='top-0 right-0 absolute'>
        <div className='flex items-center'>
          <label className='mb-0 mr-2' htmlFor='email-alerts'>
            Activar
          </label>
          <Switch
            size='md'
            defaultChecked={props.messageTemplate.status === 'active'}
            onCheckedChange={debounceSwitch}
          />
        </div>
      </div>

      <div className='py-8'>
        <MessageTemplateEditorV2
          gatewayStatus={props.messageTemplate.gatewayStatus}
          defaultValues={props.messageTemplate.template}
          onSubmit={(data) =>
            onUpdateMessageTemplate(data, () => {
              toast({
                description: 'Plantilla de mensaje actualizada',
                variant: 'success',
              })
            })
          }
          formId={formId}
          channel={props.messageTemplate.channel}
        >
          <Button
            className='mt-4'
            loading={isLoading}
            disabled={isLoading}
            type='submit'
            form={formId}
          >
            Guardar
          </Button>
        </MessageTemplateEditorV2>
      </div>
    </div>
  )
}

const MultipleMessageTemplatesDetail = (props: {
  messageTemplates: IMessageTemplate[]
  channel: IMessageTemplate['channel']
}) => {
  const [currentMessageTemplateId, setCurrentMessageTemplateId] = useState('')

  const currentMessageTemplate = props.messageTemplates.find(
    (messageTemplate) => messageTemplate._id === currentMessageTemplateId,
  )

  return (
    <div>
      <div>
        <div className='flex flex-row items-center'>
          <label>Selecciona una plantilla:</label>
          <Select onValueChange={setCurrentMessageTemplateId}>
            <SelectTrigger className='max-w-2xl w-full bg-white'>
              <SelectValue placeholder='Ninguna seleccionada' />
            </SelectTrigger>
            <SelectContent>
              {props.messageTemplates.map((messageTemplate) => {
                return (
                  <SelectItem
                    key={messageTemplate._id}
                    value={messageTemplate._id}
                  >
                    {messageTemplate._id}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
        <CreateMessageTemplate channel={props.channel} />
      </div>

      {currentMessageTemplate && (
        <div>
          <Separator className='my-10' />
          <div>
            <p className='text-gray-700 font-bold text-2xl'>EDITOR</p>
            <MessageTemplateEdit
              key={currentMessageTemplate._id}
              messageTemplate={currentMessageTemplate}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export const MessageTemplatesV2 = (props: {
  messageTemplates: IMessageTemplate[]
}) => {
  const {
    currentChannel,
    currentMessageTemplates,
    onCreateMessageTemplate,
    setTabIndex,
    isLoading,
    tabIndex,
    channels,
    multiple,
    createMessageTemplate,
    setCreateMessageTemplate,
    formId,
  } = useMessageTemplate({
    messageTemplates: props.messageTemplates,
  })

  const { toast } = useToast()

  return (
    <div>
      <TimelineStyles />
      <div className='py-2'>
        <div className='mt-5 min-w-[20rem]'>
          <Tabs
            onValueChange={(value) => {
              const index = channels.findIndex((c) => c === value)
              setTabIndex(index)
            }}
            defaultValue={channels[0]}
            className='w-[400px]'
          >
            <TabsList>
              {channels.map((channel) => {
                return (
                  <TabsTrigger
                    key={channel}
                    className='uppercase'
                    value={channel}
                  >
                    {TranslatorOnlyString({ children: channel })}
                  </TabsTrigger>
                )
              })}
            </TabsList>
          </Tabs>
          <div className='mt-5' key={currentChannel}>
            {!currentMessageTemplates.length ? (
              <div className='flex flex-col w-full items-center justify-center'>
                <BiMessageAltError
                  fontSize={'3rem'}
                  className='text-gray-500 text-[3rem]'
                />
                <div>
                  <p className='text-sm'>
                    No hay plantilla de mensaje para este canal
                  </p>
                </div>
                <CreateMessageTemplateV2 channel={currentChannel} />
              </div>
            ) : multiple ? (
              <MultipleMessageTemplatesDetail
                channel={currentChannel}
                messageTemplates={currentMessageTemplates}
              />
            ) : (
              <MessageTemplateEdit
                messageTemplate={currentMessageTemplates[0]}
              />
            )}
          </div>

          <Dialog>
            <DialogContent className='max-w-6xl w-full'>
              <DialogHeader>
                <DialogTitle>
                  Crear plantilla para{' '}
                  {TranslatorOnlyString({ children: createMessageTemplate })}
                </DialogTitle>
              </DialogHeader>
              <MessageTemplateEditorV2
                onSubmit={(data) =>
                  onCreateMessageTemplate(data, () => {
                    toast({
                      description: 'Plantilla de mensaje creada',
                      variant: 'success',
                    })
                  })
                }
                formId={formId}
                channel={createMessageTemplate}
              />
              <DialogFooter>
                <Button
                  type='submit'
                  disabled={isLoading}
                  loading={isLoading}
                  form={formId}
                  variant='primary'
                  className='mt-3'
                >
                  Crear
                </Button>
                <Button
                  disabled={isLoading}
                  onClick={() => setCreateMessageTemplate(undefined)}
                  variant='ghost'
                >
                  Cancelar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  )
}
